/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDriverThumbnail from '../../theme/useDriverThumbnail';
import Dropdown from '../common/Dropdown/Dropdown';
import Icon from '../common/Icon/Icon';
import { IconKeys } from '../common/Icon/IconKeys';
import Modal from '../common/Modal/Modal';
import ChangeFleetModal from './ChangeFleetModal/ChangeFleetModal';
import S from './UserProfileStyles';
import { BundleVersion } from '../../featuresControl';
import useGetRole from '../../API/hooks/useGetRole';
import { ROLE_MAP } from '../../constants/UserRole';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useGetFleet from '../../API/hooks/useGetFleetDetails';
import useGetCurrentUser from '../../API/hooks/users/useGetCurrentUser';
import { triggerLogout } from '../../utils/authSync';
import {
  getRecentFleets, RecentFleet, rememberOpenedFleet, setActiveFleetId,
} from '../../utils/fleetStorage';

function UserProfile(): JSX.Element {
  const profileAvatar = useDriverThumbnail();
  const { user: { attributes } } = useAuthenticator();
  const navigate = useNavigate();
  const { role, isAdmin } = useGetRole();
  const fleetId = useGetFleetId();
  const { data: fleetData } = useGetFleet(fleetId);
  const { data: currentUser } = useGetCurrentUser(fleetId, attributes?.email);
  const displayName = currentUser?.name || attributes?.email || '';
  const displayEmail = attributes?.email || '';
  const [changeFleetIsOpen, toggleChangeFleet] = useState(false);
  const [recentFleets, setRecentFleets] = useState<RecentFleet[]>(() => getRecentFleets(displayEmail));

  // every fleet opened by an admin (switched here or via the fleetId query param)
  // lands in the recently opened list
  useEffect(() => {
    if (!isAdmin || !displayEmail || !fleetId || !fleetData?.name) {
      return;
    }

    setRecentFleets(rememberOpenedFleet(displayEmail, { id: fleetId, name: fleetData.name }));
  }, [isAdmin, displayEmail, fleetId, fleetData?.name]);

  const signoutHandler = (): void => {
    navigate('/home');
    triggerLogout();
  };

  const fleetSelectHandler = (fleet: RecentFleet): void => {
    toggleChangeFleet(false);

    if (fleet.id === fleetId) {
      return;
    }

    rememberOpenedFleet(displayEmail, fleet);
    setActiveFleetId(fleet.id);
    // the fleet id is a part of nearly every request, cache key and stored page state,
    // so the app is reloaded instead of invalidating all of them one by one
    window.location.assign('/home');
  };

  return (
    <>
      <Dropdown
        target={(
          <S.UserWrapper>
            <img src={profileAvatar} alt="user-avatar" />
            <Icon iconKey={IconKeys.ARROW_DOWN} />
          </S.UserWrapper>
      )}
        contentClassName="user-dropdown"
      >
        <S.DropdownWrapper>
          <li>
            <div className="avatar">
              <img src={profileAvatar} alt="user" />
            </div>
            <div className="description">
              <p className="title">{displayName}</p>
              <p className="subtitle">{displayEmail}</p>
            </div>
          </li>
          <li
            className={isAdmin ? 'fleet-row' : ''}
            onClick={isAdmin ? () => toggleChangeFleet(true) : undefined}
          >
            <p>Fleet name: {fleetData?.name}</p>
            {isAdmin && <p className="change-fleet">Change</p>}
          </li>
          <li>
            <p>Role: {ROLE_MAP[role]}</p>
          </li>
          <li>
            <p>App version: {BundleVersion}</p>
          </li>
          <li onClick={signoutHandler}>
            <Icon iconKey={IconKeys.LOG_OUT} />
            <p>Log out</p>
          </li>
        </S.DropdownWrapper>
      </Dropdown>
      {isAdmin && changeFleetIsOpen && (
        <Modal
          title="Change fleet"
          state={changeFleetIsOpen}
          toggleState={toggleChangeFleet}
          settings={{ minWidth: '520px', maxWidth: '640px', display: 'block' }}
        >
          <ChangeFleetModal
            activeFleetId={fleetId}
            recentFleets={recentFleets}
            onSelect={fleetSelectHandler}
          />
        </Modal>
      )}
    </>
  );
}

export default UserProfile;
